/**
 * Здесь описаны объекты API такие какие они есть.
 *
 * Это приватные определения ресурсов API. Т.е. их НЕЛЬЗЯ использовать в других приложениях.
 *
 * Q: Почему абстрактные классы?
 * A: Это единственный способ прикрепить к интерфейсу метаданные.
 *
 * Q: А почему все свойства абстрактные?
 * A: Потому что включен "strictPropertyInitialization".
 */

export type Color = string | { r: number; g: number; b: number; a: number };

export abstract class ColorRangeBoundary {
  abstract value: number;
  abstract color: Color;
}

export abstract class ColorRange {
  abstract fieldName: string;
  abstract start: ColorRangeBoundary;
  abstract end: ColorRangeBoundary;
}

export abstract class LayerPatternStyle {
  abstract img: string | null;
  /**
   * 'x' | 'y' | 'xy'
   */
  abstract repetition: string | null;
}

export abstract class LayerFillStyle {
  abstract color?: Color;
  abstract colorRange?: ColorRange;
  abstract pattern?: LayerPatternStyle;
  /**
   * @deprecated
   */
  abstract type?: string;
}

export abstract class LinePatternStyle {
  abstract img: string;
  abstract width: number;
  abstract height: number;
  abstract distance: number;
}

export abstract class LayerLineStyle {
  abstract color: Color;
  abstract colorRange?: ColorRange;
  /**
   * 'butt' | 'round'
   */
  abstract lineCap: string;
  /**
   * 'bevel' | 'round' | 'miter'
   */
  abstract lineJoin: string;
  /**
   * number[] - deprecated
   */
  abstract lineDash?: string[] | null | number[];
  abstract width: number;
  /**
   * @deprecated
   */
  abstract lineDashOffset?: number;
  /**
   * @deprecated
   */
  abstract miterLimit?: number;
  abstract pattern?: LinePatternStyle;
}

export abstract class LayerPolygonStyle {
  abstract border: LayerLineStyle;
  abstract fill: LayerFillStyle;
}

export abstract class LayerTextStyle {
  abstract fill: Color;
  abstract colorRange?: ColorRange;
  abstract font: string;
  abstract offsetX: number;
  abstract offsetY: number;
  abstract minResolution?: number | null;
  abstract maxResolution?: number | null;
  abstract text: string | null;
  /**
   * 'left' | 'right' | 'center' | 'end' | 'start'
   */
  abstract textAlign: string;
  /**
   * 'bottom'| 'top'| 'middle'| 'alphabetic'| 'hanging'| 'ideographic'
   */
  abstract textBaseline: string;
  abstract stroke?: LayerLineStyle;
  /**
   * @deprecated
   */
  abstract scale?: number;
  /**
   * @deprecated
   */
  abstract rotateWithView?: boolean;
  /**
   * @deprecated
   */
  abstract rotation?: number;
  abstract overflow?: boolean;
}

export abstract class LayerCircleStyle {
  abstract radius: number;
  abstract style: LayerPolygonStyle;
}

export abstract class LayerIconStyle {
  abstract src: string;
  // tintWithColor: boolean;
  abstract opacity: number;
  abstract rotateWithView: boolean;
  abstract color?: Color;
  abstract scale: number | null;
  abstract normalizeScale: boolean;
  abstract rotation: number | null;
  /**
   * @deprecated
   */
  abstract type?: string;
  abstract relativeScale?: number;
}

export abstract class LayerPointStyle {
  abstract circle?: LayerCircleStyle;
  abstract icon?: LayerIconStyle;
  /**
   * @deprecated
   */
  abstract type?: string;
}

export abstract class LayerFeatureStyle {
  abstract point?: LayerPointStyle;
  abstract line?: LayerLineStyle;
  abstract polygon?: LayerPolygonStyle;
  abstract text?: LayerTextStyle;
  /**
   * @deprecated
   */
  abstract type?: string;
  /**
   * @deprecated
   */
  abstract fill?: string | Color;
  /**
   * @deprecated
   */
  abstract stroke?: LayerLineStyle;
  /**
   * @deprecated
   */
  abstract radius?: number;
  abstract optical?: number;
}

export abstract class LayerExpression {
  abstract fieldName: string | null;
  abstract operator: string;
  abstract value: string | null;
}

export abstract class LayerConditionalStyle {
  abstract name: string | null;
  abstract expressions: LayerExpression[];
  abstract style: LayerFeatureStyle;
  abstract optical?: number;
}

export abstract class LayerHeatmapStyle {
  abstract radius: number;
  abstract blur: number;
  abstract gradient: string[];
}

export abstract class LayerClustermapExpression {
  abstract operator: string;
  abstract value: number;
  abstract color: Color;
}

export abstract class LayerClustermapStyle {
  abstract distance: number;
  abstract point?: LayerPointStyle;
  abstract text?: LayerTextStyle;
  abstract expression?: LayerClustermapExpression[];
}

export abstract class LayerStyle {
  abstract default: LayerFeatureStyle;
  abstract conditional: LayerConditionalStyle[];
  abstract heatmap?: LayerHeatmapStyle;
  abstract clustermap?: LayerClustermapStyle;

  static DEFAULT_COLOR = '#000000';
  static DEFAULT_POINT_RADIUS = 10;
  static DEFAULT_LINE_STYLE: LayerLineStyle = {
    color: LayerStyle.DEFAULT_COLOR,
    width: 1,
    lineCap: 'round',
    lineJoin: 'round',
    lineDash: null,
  };
  static DEFAULT_POLYGON_STYLE: LayerPolygonStyle = {
    fill: {
      color: LayerStyle.DEFAULT_COLOR,
    },
    border: LayerStyle.DEFAULT_LINE_STYLE,
  };
  static DEFAULT_POINT_STYLE: LayerPointStyle = {
    circle: {
      radius: LayerStyle.DEFAULT_POINT_RADIUS,
      style: LayerStyle.DEFAULT_POLYGON_STYLE,
    },
  };
  static createDefaultStyle(geometryType: string): LayerStyle | null {
    if (['Point', 'MultiPoint'].includes(geometryType)) {
      return {
        default: {
          point: LayerStyle.DEFAULT_POINT_STYLE,
        },
        conditional: [],
      };
    }

    if (['LineString', 'MultiLineString'].includes(geometryType)) {
      return {
        default: {
          line: LayerStyle.DEFAULT_LINE_STYLE,
        },
        conditional: [],
      };
    }

    if (['Polygon', 'MultiPolygon'].includes(geometryType)) {
      return {
        default: {
          polygon: LayerStyle.DEFAULT_POLYGON_STYLE,
        },
        conditional: [],
      };
    }

    if (['Geometry', 'GeometryCollection'].includes(geometryType)) {
      return {
        default: {
          point: LayerStyle.DEFAULT_POINT_STYLE,
          line: LayerStyle.DEFAULT_LINE_STYLE,
          polygon: LayerStyle.DEFAULT_POLYGON_STYLE,
        },
        conditional: [],
      };
    }

    return null;
  }
}
